import { useEffect, useMemo, useState } from 'react';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { Pagination } from '../../components/Pagination';
import { ThemedSelect } from '../../components/ThemedSelect';
import { listProReviews, type ProReviewRow } from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

const PAGE_SIZE = 10;

function fullName(first: string | null, last: string | null): string {
  return [first, last].filter(Boolean).join(' ') || 'Anonymous';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

type SortValue = 'date' | 'rating-high' | 'rating-low';

export default function ReviewsPage() {
  const [reviews, setReviews] = useState<ProReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<SortValue>('date');

  // Pull all reviews up to the cap for the right-column stat
  // aggregation. The list view paginates client-side for display.
  // For pros with hundreds of reviews this would graduate to a
  // server-side aggregate endpoint; today the catalog is small
  // enough that loading them all once is fine.
  async function load() {
    setLoading(true);
    try {
      const res = await listProReviews({ limit: 500, offset: 0 });
      setReviews(res.data);
      setTotal(res.total_count);
    } catch (err: any) {
      toast.error(
        apiErrorMessage(err, 'Failed to load'),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Stats — compute average + tier breakdown from the loaded set.
  const stats = useMemo(() => {
    if (reviews.length === 0) {
      return {
        average: 0,
        totalReviews: 0,
        breakdown: [
          { label: 'Positive', sublabel: '4 stars and above', percentage: 0 },
          { label: 'Neutral', sublabel: '3 stars', percentage: 0 },
          { label: 'Negative', sublabel: 'under 3 stars', percentage: 0 },
        ],
      };
    }
    const sum = reviews.reduce((s, r) => s + r.rating, 0);
    const avg = sum / reviews.length;
    const positive = reviews.filter((r) => r.rating >= 4).length;
    const neutral = reviews.filter((r) => r.rating === 3).length;
    const negative = reviews.filter((r) => r.rating < 3).length;
    return {
      average: Math.round(avg * 100) / 100,
      totalReviews: reviews.length,
      breakdown: [
        {
          label: 'Positive',
          sublabel: '4 stars and above',
          percentage: Math.round((positive / reviews.length) * 100),
        },
        {
          label: 'Neutral',
          sublabel: '3 stars',
          percentage: Math.round((neutral / reviews.length) * 100),
        },
        {
          label: 'Negative',
          sublabel: 'under 3 stars',
          percentage: Math.round((negative / reviews.length) * 100),
        },
      ],
    };
  }, [reviews]);

  // Apply sort + paginate.
  const sorted = useMemo(() => {
    const arr = [...reviews];
    if (sort === 'date') {
      arr.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    } else if (sort === 'rating-high') {
      arr.sort((a, b) => b.rating - a.rating);
    } else {
      arr.sort((a, b) => a.rating - b.rating);
    }
    return arr;
  }, [reviews, sort]);

  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const visible = sorted.slice(pageStart, pageStart + PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 pb-24">
        <div className="max-w-[1140px] mx-auto px-4 py-8 md:py-16 pb-32 md:pb-16">
          <div className="flex items-center justify-between mb-5">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[32px] tracking-[-0.094px]">
              Latest reviews
            </h1>
            <ThemedSelect<SortValue>
              value={sort}
              onChange={(v) => {
                setSort(v);
                setCurrentPage(1);
              }}
              options={[
                { value: 'date', label: 'Sort by date' },
                { value: 'rating-high', label: 'Rating (high to low)' },
                { value: 'rating-low', label: 'Rating (low to high)' },
              ]}
            />
          </div>

          {loading ? (
            <p className="text-sm text-[#908e8e]">Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <p className="text-sm text-[#908e8e]">
              No reviews yet. They land here automatically when customers leave
              one.
            </p>
          ) : (
            <div className="flex flex-col lg:flex-row gap-12">
              <div className="flex-1 space-y-8">
                {visible.map((review) => (
                  <div
                    key={review.id}
                    className="bg-white border border-[#ede8e9] rounded-[12px] overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="p-6">
                      <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-4">
                        {review.service?.name ?? '—'}
                      </h3>
                      {review.comment ? (
                        <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] leading-[22px]">
                          {review.comment}
                        </p>
                      ) : (
                        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] italic">
                          Rated without a comment.
                        </p>
                      )}
                    </div>

                    <div className="bg-[#8e2a2f] px-4 py-4 md:px-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center font-bold text-white">
                          {fullName(
                            review.customer?.first_name ?? null,
                            review.customer?.last_name ?? null,
                          )
                            .slice(0, 1)
                            .toUpperCase()}
                        </div>
                        <div>
                          <p className="font-['DM_Sans',sans-serif] font-bold text-white text-[14px]">
                            {fullName(
                              review.customer?.first_name ?? null,
                              review.customer?.last_name ?? null,
                            )}
                          </p>
                          <p className="font-['DM_Sans',sans-serif] text-white/70 text-[12px]">
                            {formatDate(review.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-white">
                        <span className="material-icons text-[18px]">star</span>
                        <span className="font-['DM_Sans',sans-serif] font-bold text-[14px]">
                          {review.rating}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>

              <div className="lg:w-[320px] shrink-0">
                <div className="sticky top-[100px]">
                  <div className="mb-10">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="material-icons text-[#8e2a2f] !text-[44px]">
                        star
                      </span>
                      <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[48px] leading-none">
                        {stats.average || '—'}
                      </h2>
                    </div>
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
                      Average score based on {total} reviews
                    </p>
                  </div>

                  <div className="space-y-8">
                    {stats.breakdown.map((item) => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
                              {item.label}
                            </p>
                            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                              {item.sublabel}
                            </p>
                          </div>
                          <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
                            {item.percentage}%
                          </span>
                        </div>
                        <div className="h-[4px] w-full bg-[#f2eee2] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#8e2a2f] rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
